import {
  ChevronDownIcon,
  ChevronUpIcon,
  DragHandleIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Flex,
  FlexProps,
  Icon,
  IconButton,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import React from "react";
import { RiIndeterminateCircleLine } from "react-icons/ri";

type Props<ITEM> = {
  item: Readonly<ITEM>;
  index: number;
  itemCount: number;
  draggedIndex: number | undefined;
  provided: DraggableProvided;
  snapshot: DraggableStateSnapshot;
  render: (item: Readonly<ITEM>) => React.ReactNode;
  toLabel: (item: Readonly<ITEM>) => string;
  onMove: (offset: -1 | 1) => boolean;
  helpTextId: string | undefined;
  deleteButton: {
    onClick: (item: Readonly<ITEM>) => void;
    label: string;
    hide?: (item: Readonly<ITEM>) => boolean;
  };
  sortable: boolean;
} & FlexProps;
export function InteractiveItem<ITEM>({
  item,
  index,
  itemCount,
  draggedIndex,
  provided,
  snapshot,
  render,
  toLabel,
  onMove,
  helpTextId,
  deleteButton,
  sortable,
  ...props
}: Props<ITEM>) {
  const ref = React.useRef<HTMLDivElement>(null);
  const moveUpRef = React.useRef<HTMLButtonElement>(null);
  const moveDownRef = React.useRef<HTMLButtonElement>(null);
  const isFirst = index === 0;
  const isLast = index === itemCount - 1;

  const pendingFocusRef = React.useRef<-1 | 1 | null>(null);

  React.useEffect(() => {
    // the item's DOM node can be moved when the list reorders, which drops focus, so restore it on the pressed button.
    if (pendingFocusRef.current === -1) moveUpRef.current?.focus();
    if (pendingFocusRef.current === 1) moveDownRef.current?.focus();
    pendingFocusRef.current = null;
  }, [index]);

  const handleMove = (offset: -1 | 1) => {
    if (offset === -1 ? isFirst : isLast) return;
    pendingFocusRef.current = offset;
    // a move the list ignores doesn't change the index, so don't leave focus to be moved by a later change.
    if (!onMove(offset)) pendingFocusRef.current = null;
  };
  const [outerHeight, setOuterHeight] = React.useState<number>(0);

  React.useEffect(() => {
    if (!ref.current) {
      return;
    }
    setOuterHeight(calculateOuterHeight(ref.current));
  }, []);

  const updatedProvided = updateProvided(
    provided,
    index,
    draggedIndex,
    snapshot,
    outerHeight
  );

  return (
    <Flex
      as="li"
      height="5.5rem"
      width="100%"
      ref={ref}
      alignItems="center"
      justifyContent="space-between"
      aria-describedby={helpTextId}
    >
      {sortable && (
        <Flex direction="column" alignItems="center" flexShrink={0}>
          <Tooltip
            label="Already at the top of the list"
            hasArrow
            placement="top"
            isDisabled={!isFirst}
          >
            <IconButton
              ref={moveUpRef}
              aria-label={`Move ${toLabel(item)} up`}
              variant="ghost"
              colorScheme="primary"
              size="xs"
              icon={<ChevronUpIcon w={5} h={5} />}
              // aria-disabled instead of isDisabled keeps the button focusable, so keyboard users can reach the tooltip.
              aria-disabled={isFirst}
              onClick={() => handleMove(-1)}
            />
          </Tooltip>
          <Text
            fontWeight="600"
            textAlign="center"
            _after={{
              content: "'00'",
              display: "block",
              height: 0,
              visibility: "hidden",
            }}
          >
            {index + 1}
          </Text>
          <Tooltip
            label="Already at the bottom of the list"
            hasArrow
            placement="bottom"
            isDisabled={!isLast}
          >
            <IconButton
              ref={moveDownRef}
              aria-label={`Move ${toLabel(item)} down`}
              variant="ghost"
              colorScheme="primary"
              size="xs"
              icon={<ChevronDownIcon w={5} h={5} />}
              // aria-disabled instead of isDisabled keeps the button focusable, so keyboard users can reach the tooltip.
              aria-disabled={isLast}
              onClick={() => handleMove(1)}
            />
          </Tooltip>
        </Flex>
      )}
      <Flex
        marginLeft={1}
        marginRight={1}
        height="100%"
        flexGrow={1}
        alignItems="center"
        aria-describedby={helpTextId}
      >
        <Flex
          height="100%"
          width="100%"
          alignItems="center"
          cursor="pointer"
          ref={updatedProvided.innerRef}
          {...updatedProvided.draggableProps}
          {...updatedProvided.dragHandleProps}
          aria-describedby={helpTextId}
        >
          <Flex width="100%" height="4.75rem">
            {sortable && (
              <Flex
                backgroundColor="primary.500"
                width={7}
                flexShrink={0}
                borderLeftRadius="md"
                borderColor="primary.500"
                borderWidth={1}
                borderStyle="solid"
                height="100%"
                alignItems="center"
                justifyContent="center"
              >
                <DragHandleIcon color="white" w={3} h={3} />
              </Flex>
            )}
            <Flex
              bg="white"
              borderRightRadius="md"
              {...(sortable ? {} : { borderLeftRadius: "md" })}
              borderColor="gray.300"
              flexGrow={1}
              paddingRight={3}
              {...(sortable ? {} : { paddingLeft: 3 })}
              borderWidth={1}
              borderStyle="solid"
              height="100%"
              alignItems="center"
              {...(sortable ? { borderLeft: "none" } : {})}
              paddingX={3}
              fontSize="sm"
              fontWeight="600"
              // props style the item's content, not the whole row with the move and remove buttons.
              {...props}
            >
              {render(item)}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      {deleteButton.hide === undefined || !deleteButton.hide(item) ? (
        <IconButton
          aria-label={deleteButton.label}
          variant="link"
          colorScheme="red"
          size="xs"
          icon={<Icon w={5} h={5} as={RiIndeterminateCircleLine} />}
          onClick={() => deleteButton.onClick(item)}
        />
      ) : (
        <Box width="6" />
      )}
    </Flex>
  );
}

function updateProvided(
  provided: DraggableProvided,
  index: number,
  draggedIndex: number | undefined,
  snapshot: DraggableStateSnapshot,
  outerHeight: number
): DraggableProvided {
  if (!snapshot.isDragging) {
    if (draggedIndex !== undefined) {
      if (draggedIndex < index) {
        // for card below the dragged card, we need to adjust the translate position based on the outer height of the card.
        const transform = provided.draggableProps.style?.transform
          ? undefined
          : `translate(0px, -${outerHeight}px)`;
        return updateTransform(provided, transform);
      }
    } else {
      // this to handle a period of time where dragging has started, but we don't have draggedIndex value yet.
      // in this case, don't do transformation so we don't see the element jumping around.
      return updateTransform(provided, undefined);
    }
  }

  return provided;
}

function updateTransform(
  provided: DraggableProvided,
  transform: string | undefined
): DraggableProvided {
  const style = provided.draggableProps.style
    ? { ...provided.draggableProps.style, transform }
    : provided.draggableProps.style;
  const draggableProps = {
    ...provided.draggableProps,
    style,
  };
  const updated = {
    ...provided,
    draggableProps,
  };

  return updated;
}

function calculateOuterHeight(el: HTMLDivElement) {
  let height = el.offsetHeight;
  const style = getComputedStyle(el);

  height += parseInt(style.marginTop) + parseInt(style.marginBottom);
  return height;
}
